﻿/**
 * Vẽ một khung caption. KHÔNG clear canvas — caller lo nền.
 *
 * Cách vẽ "full-line + overlay": `fillText` cả dòng để Canvas tự lo spacing,
 * kerning và dấu, rồi vẽ hộp highlight ở vị trí từ đang đọc và tô lại đúng từ đó.
 * Toàn bộ hình học lấy từ ./captionLayout.ts.
 *
 * Bốn độ trong suốt trên cùng một dòng chữ là độc lập: `text.opacity` phủ nét
 * chữ, `outline.opacity` phủ viền, `shadow.opacity` phủ bóng, và
 * `highlight.textOpacity` phủ riêng từ đang đọc.
 */
import type {
  CaptionFrame, CaptionTextCase, RenderWord, ResolvedCaptionStyle,
} from '../schema/caption.js';
import {
  captionScale, classifyFrame, typeArea, resolveBlockWidth, positionBlock,
  maxVisibleLines, LINE_HEIGHT_FACTOR,
} from './captionLayout.js';
import { measureAndWrapText, type MeasuredLine } from './wrap.js';

/** CSS bold là 700. Chip BOLD chỉ được làm ĐẬM THÊM: gán thẳng 700 sẽ kéo nhẹ
 *  một face 900, mà tài liệu nói rõ face thật không được faux-bold lần hai. */
const BOLD_WEIGHT = 700;

/** Hộp highlight chừa trên nhiều hơn dưới để phủ dấu chồng tiếng Việt (ầ, ể, ồ). */
const HL_PAD_TOP = 0.35;
const HL_PAD_BOTTOM = 0.15;
const HL_PAD_X = 0.12;

/** Gạch chân / gạch ngang: Canvas không có API decoration nên phải tự vẽ. Độ lệch
 *  tính từ `ly` — tâm hộp em, vì textBaseline là 'middle' — nên baseline nằm quanh
 *  `ly + 0.37em` (đo trên sans-serif 48px: ascent 18.3, descent 27.7). */
const UNDERLINE_OFFSET = 0.45;
const DECORATION_THICKNESS = 0.06;
/** Khi font không trả metric dùng được — đúng với sans-serif nói trên. */
const STRIKE_OFFSET_FALLBACK = 0.12;

function applyTextCase(text: string, textCase: CaptionTextCase): string {
  switch (textCase) {
    case 'UPPERCASE': return text.toUpperCase();
    case 'LOWERCASE': return text.toLowerCase();
    // Ngữ nghĩa CSS `capitalize`: chỉ hoa chữ đầu mỗi từ, KHÔNG hạ phần còn lại.
    case 'CAPITALIZE': return text.replace(/(^|\s)(\S)/g, (_, sep, ch) => sep + ch.toUpperCase());
    default: return text;
  }
}

function fontString(style: ResolvedCaptionStyle, fontSize: number): string {
  const { fontFamily, fontWeight, formats } = style.text;
  const weight = formats.includes('BOLD') ? Math.max(fontWeight, BOLD_WEIGHT) : fontWeight;
  const italic = formats.includes('ITALIC') ? 'italic ' : '';
  return `${italic}${weight} ${fontSize}px '${fontFamily}', sans-serif`;
}

function findActiveWord(words: RenderWord[], time: number): number {
  for (let i = 0; i < words.length; i++) {
    if (time >= words[i].start && time < words[i].end) return i;
  }
  return -1;
}

/**
 * Trang chứa từ đang đọc, cho `BY_SPEECH`. Trang CỐ ĐỊNH (0..cap-1, cap..2cap-1)
 * chứ không cửa sổ trượt, để chữ không nhảy từng từ. Giữa hai từ thì neo vào từ
 * gần nhất theo thời gian, không có nó khoảng lặng sẽ nhảy về trang 0.
 */
function pageOf(
  lines: MeasuredLine[], cap: number, activeWordIdx: number, words: RenderWord[], time: number,
): MeasuredLine[] {
  if (lines.length <= cap) return lines;

  let target = activeWordIdx;
  if (target < 0) {
    let best = Infinity;
    for (let i = 0; i < words.length; i++) {
      const d = Math.min(Math.abs(time - words[i].start), Math.abs(time - words[i].end));
      if (d < best) { best = d; target = i; }
    }
  }
  let activeLine = 0;
  for (let li = 0; li < lines.length; li++) {
    if (lines[li].words.some((w) => w.wordIndex === target)) { activeLine = li; break; }
  }
  const start = Math.floor(activeLine / cap) * cap;
  return lines.slice(start, Math.min(lines.length, start + cap));
}

export function drawCaption(ctx: CanvasRenderingContext2D, frame: CaptionFrame): void {
  const { caption, style, time, canvasWidth, canvasHeight } = frame;
  if (!caption?.text) return;

  const scale = captionScale(canvasWidth, canvasHeight);
  const area = typeArea(canvasWidth, canvasHeight);
  const fontSize = style.text.fontSize * scale;
  const lineHeight = fontSize * LINE_HEIGHT_FACTOR;

  const displayText = applyTextCase(caption.text, style.text.textCase).trim();
  if (!displayText) return;
  // Xuống dòng người dùng gõ là ngắt cứng; phần còn lại vẫn ngắt theo bề rộng.
  const parts = displayText.split(/(\s+)/);
  const allWords: string[] = [];
  const breakAfter = new Set<number>();
  parts.forEach((part, index) => {
    if (index % 2 === 0) {
      if (part) allWords.push(part);
    } else if (part.includes('\n') && allWords.length) {
      breakAfter.add(allWords.length - 1);
    }
  });

  ctx.font = fontString(style, fontSize);
  ctx.textBaseline = 'middle';

  // Sàn động cần bề rộng từ dài nhất, tức phải đo trước khi biết bề rộng khối.
  // Giữ lại số đo để `measureAndWrapText` không đo lượt hai.
  const widths = allWords.map((w) => ctx.measureText(w).width);
  let longestWord = 0;
  for (const w of widths) if (w > longestWord) longestWord = w;
  const block = resolveBlockWidth(
    style.layout.blockWidth, area, longestWord, classifyFrame(canvasWidth, canvasHeight),
  );

  const words = caption.words ?? [];
  const bySpeech =
    style.layout.revealMode === 'BY_SPEECH' && words.length > 0;
  const lines = measureAndWrapText(ctx, allWords, {
    maxWidth: block.width,
    fontSize,
    lineHeight,
    align: style.text.align,
    maxWordsPerLine: bySpeech ? style.layout.maxWords : undefined,
    charBreak: longestWord > area.width,
    widths,
    breakAfter,
  });
  if (lines.length === 0) return;

  const activeWordIdx = findActiveWord(words, time);
  // BY_SPEECH bị chặn bởi maxLines; ALL_TEXT bởi chiều cao bát chữ, dòng tràn bỏ.
  const visible = bySpeech
    ? pageOf(lines, style.layout.maxLines, activeWordIdx, words, time)
    : lines.slice(0, maxVisibleLines(area.height, lineHeight));

  const blockHeight = visible.length * lineHeight;
  // Định vị theo HỘP CHỮ: độ giãn nền không tham gia, nếu không thì cùng một
  // `position` cho vị trí chữ khác nhau giữa các style chỉ vì nền giãn khác.
  const origin = positionBlock(style.position, block.width, blockHeight, area);
  const yBase = visible[0].y;

  drawBackground(ctx, style, visible, origin, block.width, blockHeight, fontSize, scale);

  const highlighting = style.highlight.enabled && activeWordIdx >= 0;
  for (const line of visible) {
    const ly = origin.y + (line.y - yBase) + lineHeight / 2;
    drawLine(ctx, style, line, origin.x + line.x, ly, fontSize, scale);
    // origin.x, KHÔNG phải mép dòng: `MeasuredWord.x` đã gồm lề trái của dòng.
    if (highlighting) drawHighlight(ctx, style, line, origin.x, ly, fontSize, activeWordIdx);
  }
}

/** Nền bám HỘP CHỮ (hợp các dòng đã vẽ), không bám mép khối: `blockWidth: 100`
 *  với một dòng ngắn thì nền phủ trọn bát chữ mới là sai. */
function drawBackground(
  ctx: CanvasRenderingContext2D, style: ResolvedCaptionStyle, lines: MeasuredLine[],
  origin: { x: number; y: number }, blockWidth: number, blockHeight: number,
  fontSize: number, scale: number,
): void {
  const bg = style.background;
  if (!bg.enabled) return;

  let inkLeft = Infinity;
  let inkRight = -Infinity;
  for (const line of lines) {
    if (line.x < inkLeft) inkLeft = line.x;
    if (line.x + line.width > inkRight) inkRight = line.x + line.width;
  }
  if (inkRight <= inkLeft) { inkLeft = 0; inkRight = blockWidth; }

  const padX = (bg.paddingX / 100) * fontSize;
  const padY = (bg.paddingY / 100) * fontSize;
  const x = origin.x + inkLeft - padX;
  const y = origin.y - padY;
  const w = inkRight - inkLeft + padX * 2;
  const h = blockHeight + padY * 2;

  ctx.save();
  ctx.globalAlpha = bg.opacity / 100;
  ctx.fillStyle = bg.color;
  // Blur mép nền qua ctx.filter, trong save riêng để không nhiễm sang chữ.
  if (bg.blur > 0) ctx.filter = `blur(${bg.blur * scale}px)`;
  roundRect(ctx, x, y, w, h, radiusOf(bg.borderRadius, w, h));
  ctx.fill();
  ctx.restore();
}

/** Đặt bóng lên ctx. Tách ra vì hai lượt vẽ dưới đây dùng chung. */
function applyShadow(
  ctx: CanvasRenderingContext2D, shadow: ResolvedCaptionStyle['shadow'], scale: number,
): void {
  ctx.shadowColor = hexToRgba(shadow.color, shadow.opacity / 100);
  ctx.shadowOffsetX = shadow.offsetX * scale;
  ctx.shadowOffsetY = shadow.offsetY * scale;
  ctx.shadowBlur = shadow.blur * scale;
}

function drawLine(
  ctx: CanvasRenderingContext2D, style: ResolvedCaptionStyle, line: MeasuredLine,
  lineX: number, ly: number, fontSize: number, scale: number,
): void {
  const { outline, shadow, text } = style;

  if (outline.width > 0) {
    ctx.save();
    ctx.globalAlpha = outline.opacity / 100;
    ctx.strokeStyle = outline.color;
    ctx.lineWidth = outline.width * scale;
    ctx.lineJoin = 'round';
    ctx.strokeText(line.text, lineX, ly);
    ctx.restore();
  }

  // Canvas nhân globalAlpha vào CẢ bóng, nên chữ mờ sẽ kéo bóng mờ theo —
  // trong khi bảng cấu hình cho bóng một độ trong suốt riêng. Chữ ĐỤC thì phép
  // nhân vô hại, giữ một lượt vẽ để khỏi tốn thêm một fillText mỗi dòng mỗi
  // khung; chỉ chữ MỜ mới tách bóng ra lượt riêng.
  const textAlpha = text.opacity / 100;
  const splitShadow = shadow.enabled && textAlpha < 1;

  if (splitShadow) {
    // Vẽ nét chữ ra ngoài mép trái khung rồi bù đúng khoảng đó vào offset của
    // bóng: bóng rơi lại đúng chỗ với alpha của chính nó, nét chữ không lọt vào
    // vùng nhìn thấy. textAlign là 'start' nên mép trái glyph nằm ở toạ độ
    // truyền vào, đẩy đúng bấy nhiêu là mép phải của nó về -1.
    const shift = lineX + line.width + 1;
    ctx.save();
    applyShadow(ctx, shadow, scale);
    ctx.shadowOffsetX += shift;
    ctx.fillStyle = text.color;
    ctx.fillText(line.text, lineX - shift, ly);
    ctx.restore();
  }

  ctx.save();
  if (shadow.enabled && !splitShadow) applyShadow(ctx, shadow, scale);
  ctx.globalAlpha = textAlpha;
  ctx.fillStyle = text.color;
  ctx.fillText(line.text, lineX, ly);
  // Gạch chân / gạch ngang trong CÙNG khối save để bóng phủ lên chúng như chữ.
  if (text.formats.includes('UNDERLINE')) {
    decorate(ctx, lineX, ly + fontSize * UNDERLINE_OFFSET, line.width, fontSize);
  }
  if (text.formats.includes('STRIKETHROUGH')) {
    const top = ly + strikeCenter(ctx, fontSize) - thicknessOf(fontSize) / 2;
    decorate(ctx, lineX, top, line.width, fontSize);
  }
  ctx.restore();
}

const thicknessOf = (fontSize: number): number =>
  Math.max(1, fontSize * DECORATION_THICKNESS);

const decorate = (
  ctx: CanvasRenderingContext2D,
  x: number, top: number, width: number, fontSize: number,
): void => {
  ctx.fillRect(x, top, width, thicknessOf(fontSize));
};

/** Tâm thân chữ thường so với `ly`, dương là xuống dưới. Một hằng số chung đặt
 *  vạch lệch hẳn ở font có x-height khác; 'x' thì đáy nằm trên baseline còn đỉnh
 *  đúng ở x-height, nên trung điểm hai mép nó là chỗ cần tìm. */
const strikeCenter = (ctx: CanvasRenderingContext2D, fontSize: number): number => {
  const { actualBoundingBoxAscent: up, actualBoundingBoxDescent: down } =
    ctx.measureText('x');
  if (!Number.isFinite(up) || !Number.isFinite(down) || up + down <= 0) {
    return fontSize * STRIKE_OFFSET_FALLBACK;
  }
  return (down - up) / 2;
};

/** `blockX` là mép trái KHỐI, không phải mép trái dòng: `MeasuredWord.x` đo từ
 *  khối và đã gồm lề trái do `align` sinh ra. */
function drawHighlight(
  ctx: CanvasRenderingContext2D, style: ResolvedCaptionStyle, line: MeasuredLine,
  blockX: number, ly: number, fontSize: number, activeWordIdx: number,
): void {
  // Từ bị ngắt giữa ký tự cho nhiều token cùng wordIndex; lấy trọn khoảng của chúng.
  const parts = line.words.filter((w) => w.wordIndex === activeWordIdx);
  if (!parts.length) return;
  const hl = style.highlight;
  const last = parts[parts.length - 1];
  const wordX = blockX + parts[0].x;
  const wordW = last.x + last.width - parts[0].x;

  if (hl.backgroundOpacity > 0) {
    const padX = fontSize * HL_PAD_X;
    const padTop = fontSize * HL_PAD_TOP;
    const h = fontSize + padTop + fontSize * HL_PAD_BOTTOM;
    const w = wordW + padX * 2;
    ctx.save();
    ctx.globalAlpha = hl.backgroundOpacity / 100;
    ctx.fillStyle = hl.backgroundColor;
    // Bo góc theo nửa cạnh ngắn của HỘP HIGHLIGHT, không theo hộp nền khối.
    roundRect(ctx, wordX - padX, ly - fontSize / 2 - padTop, w, h, radiusOf(hl.borderRadius, w, h));
    ctx.fill();
    ctx.restore();
  }

  ctx.save();
  ctx.globalAlpha = hl.textOpacity / 100;
  ctx.fillStyle = hl.textColor;
  ctx.fillText(parts.map((p) => p.text).join(''), wordX, ly);
  ctx.restore();
}

/** Bo góc là % nửa cạnh ngắn của hộp — là %, KHÔNG nhân hệ số scale. */
const radiusOf = (borderRadius: number, w: number, h: number): number =>
  (borderRadius / 100) * (Math.min(w, h) / 2);

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}
